import { Compass, FolderKanban, Rocket } from 'lucide-react';

import type { Guide } from '../types';

/**
 * Guest (client) guides — a small, self-contained set shown ONLY to guests
 * (the registry hides every staff module from them and this module from staff).
 * Neutral voice throughout ("your team", "the project owner") so the copy reads
 * correctly for both direct and white-labelled clients.
 */
export const GUEST_GUIDES: Guide[] = [
  {
    slug: 'guest-how-it-works',
    title: 'How this workspace works',
    module: 'guest',
    summary: 'What you can do here and how to find your way around.',
    icon: Compass,
    body: `Welcome. You've been added to one or more **projects** so you can follow the
work and take part in it, alongside the team delivering it.

### Finding your way
The menu on the left is short by design:

\`\`\`
 WORKSPACE
 ├─ Projects   the projects you're a member of
 └─ Help       these guides
\`\`\`

- **Projects** lists every project you've been given access to. You only ever see
  the projects you're a member of — nothing else.
- **Help** is where these guides live.

### Getting started
Open **Projects** and click a project to go inside it. Everything you need —
the work board, plans and documents — lives on the tabs across the top of a
project. The next guide walks through them.`,
  },
  {
    slug: 'guest-your-project',
    title: 'Inside a project',
    module: 'guest',
    summary: "A tour of a project's tabs — Work Items, Modules, Cycles, Views and Pages.",
    icon: FolderKanban,
    body: `Open a project and you'll see a row of tabs. Here's what each one is for.

### Overview
A summary of the project — its status and key details at a glance.

### Work Items
The heart of the project: the tasks that make up the work, shown as a **board**.

- Each **column** is a status (for example *Backlog → Todo → In Progress →
  Done*). A card's column tells you where that piece of work stands.
- Click a card to open it and read the full detail.
- **Drag a card** to another column to move it along, or open it and change its
  status there.
- Use **＋ New work item** at the top of a column to add one.

### Modules, Cycles & Views
Different ways the team organises the same work:

- **Modules** group related work items into a larger piece of scope.
- **Cycles** are time-boxed periods (like sprints) the work is planned into.
- **Views** are saved, filtered slices of the work items.

You can browse all three to understand how the project is planned.

### Pages
The project's **documents** — briefs, notes, specs and references — written in a
rich editor. Pages can be nested into a tree, so related documents stay together.
The next guide covers editing them.`,
  },
  {
    slug: 'guest-getting-things-done',
    title: 'Getting things done',
    module: 'guest',
    summary: 'Update a work item, add one, and write or edit a Page.',
    icon: Rocket,
    body: `A few common things you'll want to do.

### Move a task along
On the **Work Items** board, **drag a card** from one status column to the next —
or click the card to open it and pick a new status. Either way, the change is
saved and the whole team sees it.

### Add a work item
Click **＋ New work item** at the top of a status column, type a title, and it's
created in that status. Open it afterwards to add a description or more detail.

### Write or edit a Page
On the **Pages** tab, open a page (or create one) to read it. To make changes,
switch it into **edit** mode with the pencil / edit toggle, then write using the
block editor — type \`/\` for headings, lists, checklists, tables and more.

Your edits **save automatically** as you go, so there's no Save button to
remember. When you're finished, switch back to view mode.

### If something looks read-only
Some parts of a project are managed by the team and shown to you for context
only. If a control isn't available, that piece is simply owned by the project
owner — reach out to your team contact if you need a change there.`,
  },
];
